// src/index.ts
import { iconSize } from "@brexhq/metal-icon-shared";

// src/Icon.tsx
import { GenericStencil } from "@brexhq/metal-generic-stencil";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { Suspense, memo, useMemo } from "react";

// src/helpers/lazyHelpers.ts
import { lazy } from "react";
var lazyLoadCache = {};
function getCachedLazyComponent(key) {
  return lazyLoadCache[key];
}
function addLazyComponentToCache(key, lazyComponent) {
  lazyLoadCache[key] = lazyComponent;
}
function addResolvedComponentToCache(key, Icon3) {
  lazyLoadCache[key] = Icon3;
}
function lazyLoad(key, importFn) {
  return () => {
    const cachedComponent = getCachedLazyComponent(key);
    if (cachedComponent) {
      return cachedComponent;
    }
    const lazyComponent = lazy(async () => {
      const { default: Icon3 } = await importFn();
      addResolvedComponentToCache(key, Icon3);
      return { default: Icon3 };
    });
    addLazyComponentToCache(key, lazyComponent);
    return lazyComponent;
  };
}

// src/iconsByCategory/index.ts
var lazilyLoadedIcons = {
  aI: lazyLoad("aI", () => import("./AI-L2I2YL6H.mjs")),
  aIFilled: lazyLoad("aIFilled", () => import("./AIFilled-AOOJPD3R.mjs")),
  autopay: lazyLoad("autopay", () => import("./Autopay-ROQLFDWA.mjs")),
  bank: lazyLoad("bank", () => import("./Bank-CAAKG2WM.mjs")),
  bankNote: lazyLoad("bankNote", () => import("./BankNote-Q4PT56ZV.mjs")),
  briefcase: lazyLoad("briefcase", () => import("./Briefcase-4AZWAMJ4.mjs")),
  cashAccountNumber: lazyLoad(
    "cashAccountNumber",
    () => import("./CashAccountNumber-FEDROTLP.mjs")
  ),
  cashAddFunds: lazyLoad(
    "cashAddFunds",
    () => import("./CashAddFunds-O5JJB5HW.mjs")
  ),
  cashInflow: lazyLoad("cashInflow", () => import("./CashInflow-KTQEEUL3.mjs")),
  cashLightning: lazyLoad(
    "cashLightning",
    () => import("./CashLightning-EBEG2YTS.mjs")
  ),
  cashLocked: lazyLoad("cashLocked", () => import("./CashLocked-XKHWN5ZG.mjs")),
  cashOff: lazyLoad("cashOff", () => import("./CashOff-YDLXBM72.mjs")),
  cashOutflow: lazyLoad("cashOutflow", () => import("./CashOutflow-5BTXCVPE.mjs")),
  cashtoCash: lazyLoad("cashtoCash", () => import("./CashtoCash-FGLOWL3I.mjs")),
  cashtoCashLocked: lazyLoad(
    "cashtoCashLocked",
    () => import("./CashtoCashLocked-GZIS6T32.mjs")
  ),
  cheque: lazyLoad("cheque", () => import("./Cheque-FTV4QJUP.mjs")),
  chequeDeposit: lazyLoad(
    "chequeDeposit",
    () => import("./ChequeDeposit-OAEO2CFD.mjs")
  ),
  chequeLocked: lazyLoad(
    "chequeLocked",
    () => import("./ChequeLocked-Y2C6QCSX.mjs")
  ),
  dollar: lazyLoad("dollar", () => import("./Dollar-2TKIAL7J.mjs")),
  dollarZero: lazyLoad("dollarZero", () => import("./DollarZero-RCPQ5BYA.mjs")),
  flagUnitedStates: lazyLoad(
    "flagUnitedStates",
    () => import("./FlagUnitedStates-OK227IR3.mjs")
  ),
  global: lazyLoad("global", () => import("./Global-75JJ2MB5.mjs")),
  globalAdvanced: lazyLoad(
    "globalAdvanced",
    () => import("./GlobalAdvanced-SVXVAOSS.mjs")
  ),
  globalReimbursement: lazyLoad(
    "globalReimbursement",
    () => import("./GlobalReimbursement-LJVD6O5D.mjs")
  ),
  inbox: lazyLoad("inbox", () => import("./Inbox-BE6R4KGF.mjs")),
  inboxFilled: lazyLoad("inboxFilled", () => import("./InboxFilled-DRO4CETG.mjs")),
  invoice: lazyLoad("invoice", () => import("./Invoice-S4I3MCXY.mjs")),
  invoiceAttached: lazyLoad(
    "invoiceAttached",
    () => import("./InvoiceAttached-PJHDSIHU.mjs")
  ),
  invoiceOutgoing: lazyLoad(
    "invoiceOutgoing",
    () => import("./InvoiceOutgoing-UEC76EYO.mjs")
  ),
  invoiceScanned: lazyLoad(
    "invoiceScanned",
    () => import("./InvoiceScanned-M3BIUZCE.mjs")
  ),
  reimbursements: lazyLoad(
    "reimbursements",
    () => import("./Reimbursements-4J6O2PZU.mjs")
  ),
  statement: lazyLoad("statement", () => import("./Statement-HA4GI25D.mjs")),
  transfer: lazyLoad("transfer", () => import("./Transfer-MARFK5UG.mjs")),
  vendor: lazyLoad("vendor", () => import("./Vendor-VZGLDRQT.mjs")),
  vendorOutgoing: lazyLoad(
    "vendorOutgoing",
    () => import("./VendorOutgoing-RQXX2JAJ.mjs")
  ),
  card: lazyLoad("card", () => import("./Card-JTZ6O4OX.mjs")),
  cardActivate: lazyLoad("cardActivate", () => import("./CardActivate-7NKKPNQH.mjs")),
  cardCreate: lazyLoad("cardCreate", () => import("./CardCreate-RXOSRING.mjs")),
  cardCreditRule: lazyLoad(
    "cardCreditRule",
    () => import("./CardCreditRule-QL6G4NQD.mjs")
  ),
  cardFilled: lazyLoad("cardFilled", () => import("./CardFilled-V7KSQXRA.mjs")),
  cardTerminate: lazyLoad(
    "cardTerminate",
    () => import("./CardTerminate-WMBRGKTJ.mjs")
  ),
  cardVendor: lazyLoad("cardVendor", () => import("./CardVendor-SOGUER57.mjs")),
  cardVirtual: lazyLoad("cardVirtual", () => import("./CardVirtual-2AI5IDR6.mjs")),
  cards: lazyLoad("cards", () => import("./Cards-3E54WZNH.mjs")),
  cardsFilled: lazyLoad("cardsFilled", () => import("./CardsFilled-Z54U4FCN.mjs")),
  card1B21: lazyLoad("card1B21", () => import("./Card1B21-O4Y3ALLF.mjs")),
  cardBlack2022: lazyLoad(
    "cardBlack2022",
    () => import("./CardBlack2022-DKSTO7R2.mjs")
  ),
  cardBudget: lazyLoad(
    "cardBudget",
    () => import("./CardBudget-YOWQFYEF.mjs")
  ),
  cardCopper2022: lazyLoad(
    "cardCopper2022",
    () => import("./CardCopper2022-QNDN46EK.mjs")
  ),
  cardMetalClassic: lazyLoad(
    "cardMetalClassic",
    () => import("./CardMetalClassic-EOBTSLRU.mjs")
  ),
  cardPlatinum2022: lazyLoad(
    "cardPlatinum2022",
    () => import("./CardPlatinum2022-2RM67A56.mjs")
  ),
  barChart: lazyLoad("barChart", () => import("./BarChart-P7NBM5WG.mjs")),
  chartUp: lazyLoad("chartUp", () => import("./ChartUp-3STBDKKD.mjs")),
  circle: lazyLoad("circle", () => import("./Circle-P4Q54PKQ.mjs")),
  donutChart: lazyLoad("donutChart", () => import("./DonutChart-OR3HB6NS.mjs")),
  list: lazyLoad("list", () => import("./List-DLRZR4OE.mjs")),
  pieChart: lazyLoad("pieChart", () => import("./PieChart-IDUMXLJF.mjs")),
  pieChartFilled: lazyLoad(
    "pieChartFilled",
    () => import("./PieChartFilled-ELTE4GVP.mjs")
  ),
  scatterPlotChart: lazyLoad(
    "scatterPlotChart",
    () => import("./ScatterPlotChart-RZSKIC77.mjs")
  ),
  sort: lazyLoad("sort", () => import("./Sort-6Z7K4Q5I.mjs")),
  stackedBarChart: lazyLoad(
    "stackedBarChart",
    () => import("./StackedBarChart-VUJF7E6D.mjs")
  ),
  status: lazyLoad("status", () => import("./Status-LWYHBEX4.mjs")),
  trendingDown: lazyLoad("trendingDown", () => import("./TrendingDown-PJQK65XU.mjs")),
  trendingDownBorder: lazyLoad(
    "trendingDownBorder",
    () => import("./TrendingDownBorder-KA2QCMQB.mjs")
  ),
  trendingNeutralBorder: lazyLoad(
    "trendingNeutralBorder",
    () => import("./TrendingNeutralBorder-KICCGZEJ.mjs")
  ),
  trendingUp: lazyLoad("trendingUp", () => import("./TrendingUp-4SHXQDBG.mjs")),
  trendingUpBorder: lazyLoad(
    "trendingUpBorder",
    () => import("./TrendingUpBorder-I6K7EYNO.mjs")
  ),
  calendar: lazyLoad("calendar", () => import("./Calendar-2L5DMXAB.mjs")),
  calendar15: lazyLoad("calendar15", () => import("./Calendar15-42I73ZUR.mjs")),
  calendar30: lazyLoad("calendar30", () => import("./Calendar30-5SVP6GC5.mjs")),
  calendar4: lazyLoad("calendar4", () => import("./Calendar4-6KBWVUBW.mjs")),
  calendar7: lazyLoad("calendar7", () => import("./Calendar7-RU3MPAYY.mjs")),
  calendarRange: lazyLoad(
    "calendarRange",
    () => import("./CalendarRange-FOFQW5N2.mjs")
  ),
  clock: lazyLoad("clock", () => import("./Clock-YER3LZSX.mjs")),
  clockMovingBackwards: lazyLoad(
    "clockMovingBackwards",
    () => import("./ClockMovingBackwards-STTF2NK6.mjs")
  ),
  clockMovingForward: lazyLoad(
    "clockMovingForward",
    () => import("./ClockMovingForward-GIHQ24AL.mjs")
  ),
  daily: lazyLoad("daily", () => import("./Daily-XEWFRRI7.mjs")),
  hourglass: lazyLoad("hourglass", () => import("./Hourglass-QUR54ZRE.mjs")),
  rotate24: lazyLoad("rotate24", () => import("./Rotate24-26DBMXRN.mjs")),
  moodScale1: lazyLoad("moodScale1", () => import("./MoodScale1-2QQXAJCR.mjs")),
  moodScale2: lazyLoad("moodScale2", () => import("./MoodScale2-YPX6VWGP.mjs")),
  moodScale3: lazyLoad("moodScale3", () => import("./MoodScale3-G64MQKKA.mjs")),
  moodScale4: lazyLoad("moodScale4", () => import("./MoodScale4-XWFK3ADS.mjs")),
  moodScale5: lazyLoad("moodScale5", () => import("./MoodScale5-46UTJOL6.mjs")),
  thumbsDown: lazyLoad("thumbsDown", () => import("./ThumbsDown-TA3WD3MY.mjs")),
  thumbsUp: lazyLoad("thumbsUp", () => import("./ThumbsUp-6RTUAPCH.mjs")),
  download: lazyLoad("download", () => import("./Download-5XYWSRK3.mjs")),
  fileCSVDefault: lazyLoad(
    "fileCSVDefault",
    () => import("./FileCSVDefault-ESBYDWR2.mjs")
  ),
  fileCSVError: lazyLoad("fileCSVError", () => import("./FileCSVError-MOTOVYXC.mjs")),
  fileCSVSuccess: lazyLoad(
    "fileCSVSuccess",
    () => import("./FileCSVSuccess-FCMYBYQ3.mjs")
  ),
  fileDOCDefault: lazyLoad(
    "fileDOCDefault",
    () => import("./FileDOCDefault-DIQ4KFGK.mjs")
  ),
  fileDOCError: lazyLoad("fileDOCError", () => import("./FileDOCError-2WOK6CH4.mjs")),
  fileDOCSuccess: lazyLoad(
    "fileDOCSuccess",
    () => import("./FileDOCSuccess-M56KSVFJ.mjs")
  ),
  fileGenericDefault: lazyLoad(
    "fileGenericDefault",
    () => import("./FileGenericDefault-XI54HGKE.mjs")
  ),
  fileGenericError: lazyLoad(
    "fileGenericError",
    () => import("./FileGenericError-YMZSGRIK.mjs")
  ),
  fileGenericSuccess: lazyLoad(
    "fileGenericSuccess",
    () => import("./FileGenericSuccess-CYPUJKW2.mjs")
  ),
  fileHEICDefault: lazyLoad(
    "fileHEICDefault",
    () => import("./FileHEICDefault-FTDQZXD5.mjs")
  ),
  fileHEICError: lazyLoad(
    "fileHEICError",
    () => import("./FileHEICError-WGSZYF46.mjs")
  ),
  fileHEICSuccess: lazyLoad(
    "fileHEICSuccess",
    () => import("./FileHEICSuccess-QXKA4AG7.mjs")
  ),
  fileJPEGDefault: lazyLoad(
    "fileJPEGDefault",
    () => import("./FileJPEGDefault-GNIFSMZH.mjs")
  ),
  fileJPEGError: lazyLoad(
    "fileJPEGError",
    () => import("./FileJPEGError-7WKLP3Y4.mjs")
  ),
  fileJPEGSuccess: lazyLoad(
    "fileJPEGSuccess",
    () => import("./FileJPEGSuccess-PJU5D2OV.mjs")
  ),
  filePDFDefault: lazyLoad(
    "filePDFDefault",
    () => import("./FilePDFDefault-WDF2DEEQ.mjs")
  ),
  filePDFError: lazyLoad("filePDFError", () => import("./FilePDFError-N6JTQC3K.mjs")),
  filePDFSuccess: lazyLoad(
    "filePDFSuccess",
    () => import("./FilePDFSuccess-LZ76TL53.mjs")
  ),
  filePNGDefault: lazyLoad(
    "filePNGDefault",
    () => import("./FilePNGDefault-Q6VMDWAV.mjs")
  ),
  filePNGError: lazyLoad("filePNGError", () => import("./FilePNGError-OL5TUHHH.mjs")),
  filePNGSuccess: lazyLoad(
    "filePNGSuccess",
    () => import("./FilePNGSuccess-OE64R5M6.mjs")
  ),
  filePPTDefault: lazyLoad(
    "filePPTDefault",
    () => import("./FilePPTDefault-XWXDO5AP.mjs")
  ),
  filePPTError: lazyLoad("filePPTError", () => import("./FilePPTError-23J4PM2A.mjs")),
  filePPTSuccess: lazyLoad(
    "filePPTSuccess",
    () => import("./FilePPTSuccess-7KXA7F2N.mjs")
  ),
  incoming: lazyLoad("incoming", () => import("./Incoming-GTJL4S57.mjs")),
  outgoing: lazyLoad("outgoing", () => import("./Outgoing-OSO3AARY.mjs")),
  upload: lazyLoad("upload", () => import("./Upload-4Q3NG4Z3.mjs")),
  brexLogoFlag: lazyLoad("brexLogoFlag", () => import("./BrexLogoFlag-Y4GSAUQ5.mjs")),
  facebook: lazyLoad("facebook", () => import("./Facebook-PO7OT4MW.mjs")),
  facebookColor: lazyLoad(
    "facebookColor",
    () => import("./FacebookColor-HJRU34BL.mjs")
  ),
  instagram: lazyLoad("instagram", () => import("./Instagram-YL3P5WOA.mjs")),
  linkedIn: lazyLoad("linkedIn", () => import("./LinkedIn-5MC2EMWP.mjs")),
  linkedInColor: lazyLoad(
    "linkedInColor",
    () => import("./LinkedInColor-YDKFJQKJ.mjs")
  ),
  netSuite: lazyLoad("netSuite", () => import("./NetSuite-SOV6X6NV.mjs")),
  reddit: lazyLoad("reddit", () => import("./Reddit-LOU3ZO3X.mjs")),
  redditColor: lazyLoad("redditColor", () => import("./RedditColor-YLZDUL6E.mjs")),
  slack: lazyLoad("slack", () => import("./Slack-S6NT2K7Z.mjs")),
  slackColor: lazyLoad("slackColor", () => import("./SlackColor-NYBIBQWF.mjs")),
  whatsApp: lazyLoad("whatsApp", () => import("./WhatsApp-CP45FLFK.mjs")),
  whatsAppFilled: lazyLoad(
    "whatsAppFilled",
    () => import("./WhatsAppFilled-NHPBRW6X.mjs")
  ),
  advertisingMarketing: lazyLoad(
    "advertisingMarketing",
    () => import("./AdvertisingMarketing-4VNUWIL2.mjs")
  ),
  airlineExpenses: lazyLoad(
    "airlineExpenses",
    () => import("./AirlineExpenses-O2ZLMVXW.mjs")
  ),
  barsNightlife: lazyLoad(
    "barsNightlife",
    () => import("./BarsNightlife-AIEZD32E.mjs")
  ),
  booksNewspapers: lazyLoad(
    "booksNewspapers",
    () => import("./BooksNewspapers-YXKZ7S7Y.mjs")
  ),
  carRental: lazyLoad(
    "carRental",
    () => import("./CarRental-NJ4UKXKQ.mjs")
  ),
  charity: lazyLoad("charity", () => import("./Charity-HJQMP3HL.mjs")),
  clothing: lazyLoad("clothing", () => import("./Clothing-BJQMNEJ7.mjs")),
  clubsMemberships: lazyLoad(
    "clubsMemberships",
    () => import("./ClubsMemberships-GL5KODGL.mjs")
  ),
  coffeeShops: lazyLoad(
    "coffeeShops",
    () => import("./CoffeeShops-RIO4LCLP.mjs")
  ),
  conferences: lazyLoad(
    "conferences",
    () => import("./Conferences-JMNOTHBR.mjs")
  ),
  consultantorContractor: lazyLoad(
    "consultantorContractor",
    () => import("./ConsultantorContractor-TBNBXSNU.mjs")
  ),
  digitalGoods: lazyLoad(
    "digitalGoods",
    () => import("./DigitalGoods-O57HP4JO.mjs")
  ),
  electronics: lazyLoad(
    "electronics",
    () => import("./Electronics-L2F3NPAO.mjs")
  ),
  eventExpenses: lazyLoad(
    "eventExpenses",
    () => import("./EventExpenses-4TKLRZEI.mjs")
  ),
  flowers: lazyLoad("flowers", () => import("./Flowers-P4LO6LTV.mjs")),
  foodDelivery: lazyLoad(
    "foodDelivery",
    () => import("./FoodDelivery-QUPZR7G7.mjs")
  ),
  furniture: lazyLoad(
    "furniture",
    () => import("./Furniture-ZIWUPL2R.mjs")
  ),
  gambling: lazyLoad("gambling", () => import("./Gambling-GDBLFXCZ.mjs")),
  gasFuel: lazyLoad("gasFuel", () => import("./GasFuel-4IP5AMEM.mjs")),
  generalMerchandise: lazyLoad(
    "generalMerchandise",
    () => import("./GeneralMerchandise-3VOWR6QL.mjs")
  ),
  grocery: lazyLoad("grocery", () => import("./Grocery-UIILHIAL.mjs")),
  labEquipment: lazyLoad(
    "labEquipment",
    () => import("./LabEquipment-M52C54XE.mjs")
  ),
  legalServices: lazyLoad(
    "legalServices",
    () => import("./LegalServices-QJZXA4GG.mjs")
  ),
  lodging: lazyLoad("lodging", () => import("./Lodging-N5BNTDYX.mjs")),
  medicalExpenses: lazyLoad(
    "medicalExpenses",
    () => import("./MedicalExpenses-ATKS2CXV.mjs")
  ),
  officeSupplies: lazyLoad(
    "officeSupplies",
    () => import("./OfficeSupplies-UDQCPQEN.mjs")
  ),
  other: lazyLoad("other", () => import("./Other-YSDIFRSE.mjs")),
  parking: lazyLoad("parking", () => import("./Parking-XZIYXF34.mjs")),
  publicTransportation: lazyLoad(
    "publicTransportation",
    () => import("./PublicTransportation-DFVY2HPI.mjs")
  ),
  rent: lazyLoad("rent", () => import("./Rent-UY2J244Y.mjs")),
  restaurants: lazyLoad(
    "restaurants",
    () => import("./Restaurants-EINOTFKQ.mjs")
  ),
  rideshareTaxi: lazyLoad(
    "rideshareTaxi",
    () => import("./RideshareTaxi-ZQ6N3LXY.mjs")
  ),
  servers: lazyLoad("servers", () => import("./Servers-HYOBYITE.mjs")),
  shipping: lazyLoad("shipping", () => import("./Shipping-CXFRIQ2B.mjs")),
  software: lazyLoad("software", () => import("./Software-GTC4YEIY.mjs")),
  softwareRecurring: lazyLoad(
    "softwareRecurring",
    () => import("./SoftwareRecurring-DSWVERSB.mjs")
  ),
  telephony: lazyLoad(
    "telephony",
    () => import("./Telephony-SZXBNNHW.mjs")
  ),
  trainingEducation: lazyLoad(
    "trainingEducation",
    () => import("./TrainingEducation-LKVCLJX3.mjs")
  ),
  travel: lazyLoad("travel", () => import("./Travel-Q6XNWY7A.mjs")),
  travelWiFi: lazyLoad(
    "travelWiFi",
    () => import("./TravelWiFi-4F6SUCVS.mjs")
  ),
  arrowDown: lazyLoad("arrowDown", () => import("./ArrowDown-JXDCQ3DG.mjs")),
  arrowDownLeft: lazyLoad(
    "arrowDownLeft",
    () => import("./ArrowDownLeft-LIPGKSRT.mjs")
  ),
  arrowDownRight: lazyLoad(
    "arrowDownRight",
    () => import("./ArrowDownRight-WAHSDJ4W.mjs")
  ),
  arrowLeft: lazyLoad("arrowLeft", () => import("./ArrowLeft-NQPH5UPD.mjs")),
  arrowRight: lazyLoad("arrowRight", () => import("./ArrowRight-CAC3DFIU.mjs")),
  arrowUp: lazyLoad("arrowUp", () => import("./ArrowUp-N46SEOCN.mjs")),
  arrowUpLeft: lazyLoad(
    "arrowUpLeft",
    () => import("./ArrowUpLeft-KC45RINU.mjs")
  ),
  arrowUpRight: lazyLoad(
    "arrowUpRight",
    () => import("./ArrowUpRight-VWAEIIGW.mjs")
  ),
  budget: lazyLoad("budget", () => import("./Budget-X56HNERA.mjs")),
  budgetFilled: lazyLoad(
    "budgetFilled",
    () => import("./BudgetFilled-JVQMTTJX.mjs")
  ),
  budgetNested: lazyLoad(
    "budgetNested",
    () => import("./BudgetNested-FI4HNAVG.mjs")
  ),
  cash: lazyLoad("cash", () => import("./Cash-7732YGP5.mjs")),
  cashFilled: lazyLoad("cashFilled", () => import("./CashFilled-UJ77QMYN.mjs")),
  chevronDown: lazyLoad(
    "chevronDown",
    () => import("./ChevronDown-NUDNWXRO.mjs")
  ),
  chevronLeft: lazyLoad(
    "chevronLeft",
    () => import("./ChevronLeft-SMZSNDZC.mjs")
  ),
  chevronRight: lazyLoad(
    "chevronRight",
    () => import("./ChevronRight-HG7CCJMB.mjs")
  ),
  chevronUp: lazyLoad("chevronUp", () => import("./ChevronUp-5FRZFFZ2.mjs")),
  close: lazyLoad("close", () => import("./Close-NXAD2FL5.mjs")),
  codeEditorBottom: lazyLoad(
    "codeEditorBottom",
    () => import("./CodeEditorBottom-52FNNITF.mjs")
  ),
  codeEditorRight: lazyLoad(
    "codeEditorRight",
    () => import("./CodeEditorRight-FBWGDKQG.mjs")
  ),
  home: lazyLoad("home", () => import("./Home-XW752MFU.mjs")),
  homeFilled: lazyLoad("homeFilled", () => import("./HomeFilled-RMFQT4LH.mjs")),
  integrations: lazyLoad(
    "integrations",
    () => import("./Integrations-G3ZQJCQY.mjs")
  ),
  integrationsFilled: lazyLoad(
    "integrationsFilled",
    () => import("./IntegrationsFilled-W7E5634N.mjs")
  ),
  menu: lazyLoad("menu", () => import("./Menu-IMCGZTKQ.mjs")),
  overflowHorizontal: lazyLoad(
    "overflowHorizontal",
    () => import("./OverflowHorizontal-JYYFYQA4.mjs")
  ),
  overflowVertical: lazyLoad(
    "overflowVertical",
    () => import("./OverflowVertical-AS6IQ4JL.mjs")
  ),
  productLog: lazyLoad("productLog", () => import("./ProductLog-4U5IIEO4.mjs")),
  sandbox: lazyLoad("sandbox", () => import("./Sandbox-F5BE3SJ7.mjs")),
  sandboxFilled: lazyLoad(
    "sandboxFilled",
    () => import("./SandboxFilled-FASGCLIJ.mjs")
  ),
  settings: lazyLoad("settings", () => import("./Settings-SWSHA3UV.mjs")),
  settingsFilled: lazyLoad(
    "settingsFilled",
    () => import("./SettingsFilled-4NUX2DIN.mjs")
  ),
  sidebar: lazyLoad("sidebar", () => import("./Sidebar-AS724HP3.mjs")),
  signOut: lazyLoad("signOut", () => import("./SignOut-NYYI23IZ.mjs")),
  tasks: lazyLoad("tasks", () => import("./Tasks-2JU5GQ6Z.mjs")),
  tasksFilled: lazyLoad(
    "tasksFilled",
    () => import("./TasksFilled-FD4TOSMT.mjs")
  ),
  themeDark: lazyLoad("themeDark", () => import("./ThemeDark-Z6A3AKPM.mjs")),
  themeLight: lazyLoad("themeLight", () => import("./ThemeLight-O43RPOZ5.mjs")),
  wallet: lazyLoad("wallet", () => import("./Wallet-JRWL7UJC.mjs")),
  walletFilled: lazyLoad(
    "walletFilled",
    () => import("./WalletFilled-3Z2JEDXV.mjs")
  ),
  camera: lazyLoad("camera", () => import("./Camera-2LISF47I.mjs")),
  chatSupport: lazyLoad("chatSupport", () => import("./ChatSupport-CMQHHPMJ.mjs")),
  chatSupportFilled: lazyLoad(
    "chatSupportFilled",
    () => import("./ChatSupportFilled-CP554KUJ.mjs")
  ),
  code: lazyLoad("code", () => import("./Code-Q2RAOVYA.mjs")),
  component: lazyLoad("component", () => import("./Component-WW6WRYI3.mjs")),
  computer: lazyLoad("computer", () => import("./Computer-D4MA52ZO.mjs")),
  contactless: lazyLoad("contactless", () => import("./Contactless-YGDHODRY.mjs")),
  contactlessFilled: lazyLoad(
    "contactlessFilled",
    () => import("./ContactlessFilled-TYPXKQGX.mjs")
  ),
  cursor: lazyLoad("cursor", () => import("./Cursor-7Y4IHNTV.mjs")),
  cursorClicking: lazyLoad(
    "cursorClicking",
    () => import("./CursorClicking-AWNW3YCF.mjs")
  ),
  cursorClickingFilled: lazyLoad(
    "cursorClickingFilled",
    () => import("./CursorClickingFilled-42CCWMQT.mjs")
  ),
  cursorFilled: lazyLoad(
    "cursorFilled",
    () => import("./CursorFilled-TRL7LSJX.mjs")
  ),
  data: lazyLoad("data", () => import("./Data-H5Q6W2DH.mjs")),
  dataFilled: lazyLoad("dataFilled", () => import("./DataFilled-4ZGYRA7U.mjs")),
  discount: lazyLoad("discount", () => import("./Discount-S2NYIDOR.mjs")),
  email: lazyLoad("email", () => import("./Email-OEB37Y6V.mjs")),
  emailFilled: lazyLoad("emailFilled", () => import("./EmailFilled-RK2HOHQR.mjs")),
  emailOpen: lazyLoad("emailOpen", () => import("./EmailOpen-PHHTHIDV.mjs")),
  emailOpenFilled: lazyLoad(
    "emailOpenFilled",
    () => import("./EmailOpenFilled-4T6TXCR7.mjs")
  ),
  faceID: lazyLoad("faceID", () => import("./FaceID-MJOE3JNZ.mjs")),
  giftCard: lazyLoad("giftCard", () => import("./GiftCard-534ELMJF.mjs")),
  giftCardFilled: lazyLoad(
    "giftCardFilled",
    () => import("./GiftCardFilled-3PBZD5AQ.mjs")
  ),
  headphones: lazyLoad("headphones", () => import("./Headphones-M6Z2U4KN.mjs")),
  image: lazyLoad("image", () => import("./Image-MGI3TPCP.mjs")),
  imageFilled: lazyLoad("imageFilled", () => import("./ImageFilled-VCP6IKKG.mjs")),
  mapping: lazyLoad("mapping", () => import("./Mapping-SVTH5YZJ.mjs")),
  microphone: lazyLoad("microphone", () => import("./Microphone-EFV52L75.mjs")),
  microphoneFilled: lazyLoad(
    "microphoneFilled",
    () => import("./MicrophoneFilled-VUSWKQAM.mjs")
  ),
  microphoneOff: lazyLoad(
    "microphoneOff",
    () => import("./MicrophoneOff-7IGLT3SY.mjs")
  ),
  microphoneOffFilled: lazyLoad(
    "microphoneOffFilled",
    () => import("./MicrophoneOffFilled-XZPJAO3L.mjs")
  ),
  notification: lazyLoad(
    "notification",
    () => import("./Notification-K2RXTCVE.mjs")
  ),
  notificationFilled: lazyLoad(
    "notificationFilled",
    () => import("./NotificationFilled-VMONV3UX.mjs")
  ),
  notificationOff: lazyLoad(
    "notificationOff",
    () => import("./NotificationOff-JOFAYY5H.mjs")
  ),
  notificationWithBadge: lazyLoad(
    "notificationWithBadge",
    () => import("./NotificationWithBadge-G5UQQF2H.mjs")
  ),
  notificationWithBadgeFilled: lazyLoad(
    "notificationWithBadgeFilled",
    () => import("./NotificationWithBadgeFilled-4G2F3VSS.mjs")
  ),
  phone: lazyLoad("phone", () => import("./Phone-TW4QWGPA.mjs")),
  phoneFilled: lazyLoad("phoneFilled", () => import("./PhoneFilled-DR3ZHLNB.mjs")),
  pin: lazyLoad("pin", () => import("./Pin-O2CCUALJ.mjs")),
  pinFilled: lazyLoad("pinFilled", () => import("./PinFilled-XQFONGYM.mjs")),
  policy: lazyLoad("policy", () => import("./Policy-Y4443IG2.mjs")),
  policyFilled: lazyLoad(
    "policyFilled",
    () => import("./PolicyFilled-B2WQLRFX.mjs")
  ),
  referrals: lazyLoad("referrals", () => import("./Referrals-IAKX3COZ.mjs")),
  referralsFilled: lazyLoad(
    "referralsFilled",
    () => import("./ReferralsFilled-4ZQKKKS7.mjs")
  ),
  rewards: lazyLoad("rewards", () => import("./Rewards-H5ZH5ACD.mjs")),
  rewardsFilled: lazyLoad(
    "rewardsFilled",
    () => import("./RewardsFilled-JIUP4LVK.mjs")
  ),
  rule: lazyLoad("rule", () => import("./Rule-VYZUSLNY.mjs")),
  security: lazyLoad("security", () => import("./Security-JLDISGIA.mjs")),
  securityFilled: lazyLoad(
    "securityFilled",
    () => import("./SecurityFilled-ET4RDSEL.mjs")
  ),
  securityKey: lazyLoad("securityKey", () => import("./SecurityKey-KTPK7NKD.mjs")),
  securityKeyFilled: lazyLoad(
    "securityKeyFilled",
    () => import("./SecurityKeyFilled-7EZCQXOS.mjs")
  ),
  smartphone: lazyLoad("smartphone", () => import("./Smartphone-FPXWYXLG.mjs")),
  star: lazyLoad("star", () => import("./Star-PLFSDWPF.mjs")),
  starFilled: lazyLoad("starFilled", () => import("./StarFilled-XWDVMAG2.mjs")),
  starHalfFilled: lazyLoad(
    "starHalfFilled",
    () => import("./StarHalfFilled-HMNKE4IZ.mjs")
  ),
  touchID: lazyLoad("touchID", () => import("./TouchID-ISQIA6SX.mjs")),
  unpin: lazyLoad("unpin", () => import("./Unpin-SMDRANTC.mjs")),
  unpinFilled: lazyLoad("unpinFilled", () => import("./UnpinFilled-FR6MCAP6.mjs")),
  add: lazyLoad("add", () => import("./Add-HCUFOV4M.mjs")),
  add12pxOnly: lazyLoad(
    "add12pxOnly",
    () => import("./Add12pxOnly-UNFEEKFH.mjs")
  ),
  addinCircle: lazyLoad(
    "addinCircle",
    () => import("./AddinCircle-GDW4QBAK.mjs")
  ),
  addinCircleFilled: lazyLoad(
    "addinCircleFilled",
    () => import("./AddinCircleFilled-XEYEW3AY.mjs")
  ),
  archive: lazyLoad("archive", () => import("./Archive-DMMBMYWG.mjs")),
  archiveFilled: lazyLoad(
    "archiveFilled",
    () => import("./ArchiveFilled-2V7OIMAU.mjs")
  ),
  cardLayout: lazyLoad("cardLayout", () => import("./CardLayout-LRML2SO5.mjs")),
  columns: lazyLoad("columns", () => import("./Columns-W74Q2VDJ.mjs")),
  copy: lazyLoad("copy", () => import("./Copy-GMWIIBTX.mjs")),
  copyFilled: lazyLoad("copyFilled", () => import("./CopyFilled-6TQENV2L.mjs")),
  dataGridDensityComfortable: lazyLoad(
    "dataGridDensityComfortable",
    () => import("./DataGridDensityComfortable-PMJPRCEN.mjs")
  ),
  dataGridDensityCompact: lazyLoad(
    "dataGridDensityCompact",
    () => import("./DataGridDensityCompact-4LQRPY5H.mjs")
  ),
  dataGridDensitySpacious: lazyLoad(
    "dataGridDensitySpacious",
    () => import("./DataGridDensitySpacious-23EDKBCA.mjs")
  ),
  dataGridDividers: lazyLoad(
    "dataGridDividers",
    () => import("./DataGridDividers-6LW6QVCZ.mjs")
  ),
  dataGridNoDividers: lazyLoad(
    "dataGridNoDividers",
    () => import("./DataGridNoDividers-3KQF3OYM.mjs")
  ),
  dragHandle: lazyLoad("dragHandle", () => import("./DragHandle-EQJ25WWQ.mjs")),
  edit: lazyLoad("edit", () => import("./Edit-XJPLCXNG.mjs")),
  externalLink: lazyLoad(
    "externalLink",
    () => import("./ExternalLink-2663I47N.mjs")
  ),
  eyeHide: lazyLoad("eyeHide", () => import("./EyeHide-FX2GDFBI.mjs")),
  eyeReveal: lazyLoad("eyeReveal", () => import("./EyeReveal-7NVKMDHV.mjs")),
  filter: lazyLoad("filter", () => import("./Filter-IZNFZJKJ.mjs")),
  link: lazyLoad("link", () => import("./Link-JXED5Z3G.mjs")),
  lock: lazyLoad("lock", () => import("./Lock-I5OLYQYZ.mjs")),
  lockFilled: lazyLoad("lockFilled", () => import("./LockFilled-LAVLBIZQ.mjs")),
  maximize: lazyLoad("maximize", () => import("./Maximize-UE3IT43W.mjs")),
  remove: lazyLoad("remove", () => import("./Remove-ISFHLRZT.mjs")),
  repeat: lazyLoad("repeat", () => import("./Repeat-D3SQ4ZT3.mjs")),
  return: lazyLoad("return", () => import("./Return-BM3EDBON.mjs")),
  rotate: lazyLoad("rotate", () => import("./Rotate-SEYJFWXO.mjs")),
  search: lazyLoad("search", () => import("./Search-LI4LVQ2X.mjs")),
  send: lazyLoad("send", () => import("./Send-RRUWHAZX.mjs")),
  snooze: lazyLoad("snooze", () => import("./Snooze-BUAAFFLZ.mjs")),
  snoozeFilled: lazyLoad(
    "snoozeFilled",
    () => import("./SnoozeFilled-MJV2ISQG.mjs")
  ),
  sync: lazyLoad("sync", () => import("./Sync-SX4JRC4Y.mjs")),
  tableDensity: lazyLoad(
    "tableDensity",
    () => import("./TableDensity-D3OOEFHV.mjs")
  ),
  tableFilter: lazyLoad(
    "tableFilter",
    () => import("./TableFilter-65KSKI56.mjs")
  ),
  tableGroup: lazyLoad("tableGroup", () => import("./TableGroup-XOFFDBDP.mjs")),
  trash: lazyLoad("trash", () => import("./Trash-EPZESW4T.mjs")),
  trashFilled: lazyLoad(
    "trashFilled",
    () => import("./TrashFilled-2CXW5YXT.mjs")
  ),
  typography: lazyLoad("typography", () => import("./Typography-3ZHL5JJG.mjs")),
  unlock: lazyLoad("unlock", () => import("./Unlock-Q45UW546.mjs")),
  unlockFilled: lazyLoad(
    "unlockFilled",
    () => import("./UnlockFilled-P6URCZRW.mjs")
  ),
  autogenerated: lazyLoad(
    "autogenerated",
    () => import("./Autogenerated-AXUKV4QA.mjs")
  ),
  billDenied: lazyLoad("billDenied", () => import("./BillDenied-6C42YERV.mjs")),
  billError: lazyLoad("billError", () => import("./BillError-5TKI3R47.mjs")),
  billPaid: lazyLoad("billPaid", () => import("./BillPaid-ESBTDUPM.mjs")),
  billProcessing: lazyLoad(
    "billProcessing",
    () => import("./BillProcessing-YBC4PYGT.mjs")
  ),
  billScheduled: lazyLoad(
    "billScheduled",
    () => import("./BillScheduled-GRIGMYOU.mjs")
  ),
  billSubmitted: lazyLoad(
    "billSubmitted",
    () => import("./BillSubmitted-AGINTNVQ.mjs")
  ),
  cardexpenseApproved: lazyLoad(
    "cardexpenseApproved",
    () => import("./CardexpenseApproved-PMMD4VA2.mjs")
  ),
  cardexpenseDenied: lazyLoad(
    "cardexpenseDenied",
    () => import("./CardexpenseDenied-BTWQVS44.mjs")
  ),
  cardexpenseOutofpolicy: lazyLoad(
    "cardexpenseOutofpolicy",
    () => import("./CardexpenseOutofpolicy-L3SEKZTP.mjs")
  ),
  cardexpenseSubmitted: lazyLoad(
    "cardexpenseSubmitted",
    () => import("./CardexpenseSubmitted-DBPIEXSW.mjs")
  ),
  deadlineCompleted: lazyLoad(
    "deadlineCompleted",
    () => import("./DeadlineCompleted-XV65C6FQ.mjs")
  ),
  deadlineDue: lazyLoad("deadlineDue", () => import("./DeadlineDue-QNH22HBW.mjs")),
  deadlineOverdue: lazyLoad(
    "deadlineOverdue",
    () => import("./DeadlineOverdue-XFANWT3U.mjs")
  ),
  dispute: lazyLoad("dispute", () => import("./Dispute-Q7RZE7QO.mjs")),
  disputeFilled: lazyLoad(
    "disputeFilled",
    () => import("./DisputeFilled-4LKENFPZ.mjs")
  ),
  expenses: lazyLoad("expenses", () => import("./Expenses-GWIFCHUA.mjs")),
  expensesFilled: lazyLoad(
    "expensesFilled",
    () => import("./ExpensesFilled-LVJJLQ45.mjs")
  ),
  flag: lazyLoad("flag", () => import("./Flag-L3GXZ3VQ.mjs")),
  flagFilled: lazyLoad("flagFilled", () => import("./FlagFilled-HOALNIAQ.mjs")),
  memo: lazyLoad("memo", () => import("./Memo-EXK77CR5.mjs")),
  memoAdd: lazyLoad("memoAdd", () => import("./MemoAdd-I6V4R6VR.mjs")),
  memoAdded: lazyLoad("memoAdded", () => import("./MemoAdded-HGTFCBI5.mjs")),
  memoAutogenerated: lazyLoad(
    "memoAutogenerated",
    () => import("./MemoAutogenerated-FURP22P7.mjs")
  ),
  memoMissing: lazyLoad("memoMissing", () => import("./MemoMissing-WN4FGSZL.mjs")),
  receipt: lazyLoad("receipt", () => import("./Receipt-VDZTXT4P.mjs")),
  receiptAdd: lazyLoad("receiptAdd", () => import("./ReceiptAdd-VN277NQ7.mjs")),
  receiptAdded: lazyLoad("receiptAdded", () => import("./ReceiptAdded-K3RL6JJ4.mjs")),
  receiptAutogenerated: lazyLoad(
    "receiptAutogenerated",
    () => import("./ReceiptAutogenerated-CRR3SLDN.mjs")
  ),
  receiptFilled: lazyLoad(
    "receiptFilled",
    () => import("./ReceiptFilled-ZV3SKQHD.mjs")
  ),
  receiptPending: lazyLoad(
    "receiptPending",
    () => import("./ReceiptPending-FRC67IDS.mjs")
  ),
  receiptWarning: lazyLoad(
    "receiptWarning",
    () => import("./ReceiptWarning-DTY7CWWR.mjs")
  ),
  receipts: lazyLoad("receipts", () => import("./Receipts-5YD647V6.mjs")),
  receiptsAdded: lazyLoad(
    "receiptsAdded",
    () => import("./ReceiptsAdded-NPZV6LEN.mjs")
  ),
  reimbursementCanceled: lazyLoad(
    "reimbursementCanceled",
    () => import("./ReimbursementCanceled-M7HPT3WV.mjs")
  ),
  reimbursementDenied: lazyLoad(
    "reimbursementDenied",
    () => import("./ReimbursementDenied-UFFBZS32.mjs")
  ),
  reimbursementFailed: lazyLoad(
    "reimbursementFailed",
    () => import("./ReimbursementFailed-4SSTAMX7.mjs")
  ),
  reimbursementPaid: lazyLoad(
    "reimbursementPaid",
    () => import("./ReimbursementPaid-5HFIW2FL.mjs")
  ),
  reimbursementScheduled: lazyLoad(
    "reimbursementScheduled",
    () => import("./ReimbursementScheduled-4RJZKRE3.mjs")
  ),
  reimbursementSubmitted: lazyLoad(
    "reimbursementSubmitted",
    () => import("./ReimbursementSubmitted-TFRN4WNV.mjs")
  ),
  rewardRedeemed: lazyLoad(
    "rewardRedeemed",
    () => import("./RewardRedeemed-375XCA2G.mjs")
  ),
  splitExpense: lazyLoad("splitExpense", () => import("./SplitExpense-MJOHLIH5.mjs")),
  checkmark: lazyLoad("checkmark", () => import("./Checkmark-XJASFFP2.mjs")),
  checkmarkinCircle: lazyLoad(
    "checkmarkinCircle",
    () => import("./CheckmarkinCircle-KEW5K75S.mjs")
  ),
  checkmarkinCircleFilled: lazyLoad(
    "checkmarkinCircleFilled",
    () => import("./CheckmarkinCircleFilled-DVG4LNV2.mjs")
  ),
  closeinCircle: lazyLoad(
    "closeinCircle",
    () => import("./CloseinCircle-D6HBZ4DQ.mjs")
  ),
  closeinCircleFilled: lazyLoad(
    "closeinCircleFilled",
    () => import("./CloseinCircleFilled-NXXCW26C.mjs")
  ),
  error: lazyLoad("error", () => import("./Error-IMCOS54B.mjs")),
  errorFilled: lazyLoad("errorFilled", () => import("./ErrorFilled-NN23E66L.mjs")),
  fourCorners: lazyLoad("fourCorners", () => import("./FourCorners-EAGTIL6Q.mjs")),
  help: lazyLoad("help", () => import("./Help-AWWITMSN.mjs")),
  helpFilled: lazyLoad("helpFilled", () => import("./HelpFilled-ZMBEZ3W4.mjs")),
  information: lazyLoad("information", () => import("./Information-6S4SHJQA.mjs")),
  informationFilled: lazyLoad(
    "informationFilled",
    () => import("./InformationFilled-ZWBDXQ5W.mjs")
  ),
  lightning: lazyLoad("lightning", () => import("./Lightning-WHTIMNJZ.mjs")),
  lightningFilled: lazyLoad(
    "lightningFilled",
    () => import("./LightningFilled-GVT7462H.mjs")
  ),
  lightningOff: lazyLoad("lightningOff", () => import("./LightningOff-GTUBSKKY.mjs")),
  lightningOffFilled: lazyLoad(
    "lightningOffFilled",
    () => import("./LightningOffFilled-CWOGJIIL.mjs")
  ),
  loop: lazyLoad("loop", () => import("./Loop-ECNQUBNW.mjs")),
  terminated: lazyLoad("terminated", () => import("./Terminated-SDFEIWHJ.mjs")),
  warning: lazyLoad("warning", () => import("./Warning-YJAQGQBH.mjs")),
  warningFilled: lazyLoad(
    "warningFilled",
    () => import("./WarningFilled-MG7LUU4X.mjs")
  ),
  flightArriving: lazyLoad(
    "flightArriving",
    () => import("./FlightArriving-CCWYR56E.mjs")
  ),
  flightArrivingFilled: lazyLoad(
    "flightArrivingFilled",
    () => import("./FlightArrivingFilled-EAZVNV2A.mjs")
  ),
  flightDeparting: lazyLoad(
    "flightDeparting",
    () => import("./FlightDeparting-XCRE2AAD.mjs")
  ),
  flightDepartingFilled: lazyLoad(
    "flightDepartingFilled",
    () => import("./FlightDepartingFilled-WSOBOV6L.mjs")
  ),
  flightInflight: lazyLoad(
    "flightInflight",
    () => import("./FlightInflight-LCS4D6GM.mjs")
  ),
  oneWayTrip: lazyLoad("oneWayTrip", () => import("./OneWayTrip-M3GSGBLX.mjs")),
  roundTrip: lazyLoad("roundTrip", () => import("./RoundTrip-5CAVXJNI.mjs")),
  seat: lazyLoad("seat", () => import("./Seat-BJMK53I6.mjs")),
  tripDestination: lazyLoad(
    "tripDestination",
    () => import("./TripDestination-ULKSVVFA.mjs")
  ),
  tripOrigin: lazyLoad("tripOrigin", () => import("./TripOrigin-ESGFN3YZ.mjs")),
  tripStop: lazyLoad("tripStop", () => import("./TripStop-ZLFRJEXX.mjs")),
  company: lazyLoad("company", () => import("./Company-IXASD52R.mjs")),
  department: lazyLoad("department", () => import("./Department-2GE5WPVK.mjs")),
  departmentFilled: lazyLoad(
    "departmentFilled",
    () => import("./DepartmentFilled-JEXDVOC4.mjs")
  ),
  signature: lazyLoad("signature", () => import("./Signature-KWOXYNMJ.mjs")),
  user: lazyLoad("user", () => import("./User-QEKOTNK3.mjs")),
  userAdd: lazyLoad("userAdd", () => import("./UserAdd-26SN47PV.mjs")),
  userAddFilled: lazyLoad(
    "userAddFilled",
    () => import("./UserAddFilled-ZU5UDAQZ.mjs")
  ),
  userApproved: lazyLoad("userApproved", () => import("./UserApproved-T2M2WCOP.mjs")),
  userApprovedFilled: lazyLoad(
    "userApprovedFilled",
    () => import("./UserApprovedFilled-ILD63PDW.mjs")
  ),
  userCheckmark: lazyLoad(
    "userCheckmark",
    () => import("./UserCheckmark-AKRNQLLB.mjs")
  ),
  userCheckmarkFilled: lazyLoad(
    "userCheckmarkFilled",
    () => import("./UserCheckmarkFilled-ZPPJTI7D.mjs")
  ),
  userFilled: lazyLoad("userFilled", () => import("./UserFilled-SNYLG4WW.mjs")),
  userLock: lazyLoad("userLock", () => import("./UserLock-KO5MNEJ7.mjs")),
  userLockFilled: lazyLoad(
    "userLockFilled",
    () => import("./UserLockFilled-WWU7XRT7.mjs")
  ),
  userRemove: lazyLoad("userRemove", () => import("./UserRemove-CTCR4GS7.mjs")),
  userRemoveFilled: lazyLoad(
    "userRemoveFilled",
    () => import("./UserRemoveFilled-6QD7PYNA.mjs")
  ),
  userTerminate: lazyLoad(
    "userTerminate",
    () => import("./UserTerminate-JGZBY3ZQ.mjs")
  ),
  userTerminateFilled: lazyLoad(
    "userTerminateFilled",
    () => import("./UserTerminateFilled-TDP2PBTV.mjs")
  ),
  userTransfer: lazyLoad("userTransfer", () => import("./UserTransfer-5DLXQ2E4.mjs")),
  userTransferFilled: lazyLoad(
    "userTransferFilled",
    () => import("./UserTransferFilled-CT4IRKJ6.mjs")
  ),
  users: lazyLoad("users", () => import("./Users-M5V6YPKS.mjs")),
  usersAdd: lazyLoad("usersAdd", () => import("./UsersAdd-N7Q3T3X3.mjs")),
  usersAddFilled: lazyLoad(
    "usersAddFilled",
    () => import("./UsersAddFilled-KBFLHCGQ.mjs")
  ),
  usersAutomated: lazyLoad(
    "usersAutomated",
    () => import("./UsersAutomated-P66OIOM7.mjs")
  ),
  usersAutomatedFilled: lazyLoad(
    "usersAutomatedFilled",
    () => import("./UsersAutomatedFilled-W4WCIYCY.mjs")
  ),
  usersCheckmark: lazyLoad(
    "usersCheckmark",
    () => import("./UsersCheckmark-ZTCEOQ6Z.mjs")
  ),
  usersCheckmarkFilled: lazyLoad(
    "usersCheckmarkFilled",
    () => import("./UsersCheckmarkFilled-FJ43C65M.mjs")
  ),
  usersFilled: lazyLoad("usersFilled", () => import("./UsersFilled-GVIRTECZ.mjs")),
  usersLock: lazyLoad("usersLock", () => import("./UsersLock-O4KHQGBW.mjs")),
  usersLockFilled: lazyLoad(
    "usersLockFilled",
    () => import("./UsersLockFilled-IPRNY27T.mjs")
  ),
  usersPayroll: lazyLoad("usersPayroll", () => import("./UsersPayroll-K4TOVCF5.mjs")),
  usersPayrollFilled: lazyLoad(
    "usersPayrollFilled",
    () => import("./UsersPayrollFilled-3PHT7FA5.mjs")
  ),
  usersRemove: lazyLoad("usersRemove", () => import("./UsersRemove-5WEYBL33.mjs")),
  usersRemoveFilled: lazyLoad(
    "usersRemoveFilled",
    () => import("./UsersRemoveFilled-BXZYT6HE.mjs")
  ),
  usersTerminate: lazyLoad(
    "usersTerminate",
    () => import("./UsersTerminate-2XLEARW2.mjs")
  ),
  usersTerminateFilled: lazyLoad(
    "usersTerminateFilled",
    () => import("./UsersTerminateFilled-DUIJSKWB.mjs")
  )
};

// src/Icon.tsx
import { jsx } from "react/jsx-runtime";
var StyledIconWrapper = styled.div(
  ({ size, color }) => ({
    color: color ?? "inherit",
    width: size,
    height: size,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
    flexGrow: 0
  })
);
var IconStencil = memo(({ size }) => {
  const { borders } = useTheme();
  const style = useMemo(
    () => ({ borderRadius: borders.radius.circle }),
    [borders.radius.circle]
  );
  return /* @__PURE__ */ jsx(GenericStencil, {
    width: size,
    height: size,
    style
  });
});
var BASE_STYLE = {};
var Icon = ({
  size,
  name,
  style: rawStyle = BASE_STYLE,
  color,
  ...props
}) => {
  const DesiredIcon = useMemo(() => lazilyLoadedIcons[name](), [name]);
  const style = useMemo(() => ({ ...rawStyle, flex: 1 }), [rawStyle]);
  return /* @__PURE__ */ jsx(Suspense, {
    fallback: /* @__PURE__ */ jsx(IconStencil, {
      size
    }),
    children: /* @__PURE__ */ jsx(StyledIconWrapper, {
      size,
      color,
      children: /* @__PURE__ */ jsx(DesiredIcon, {
        ...props,
        style
      })
    })
  });
};
var iconNames = Object.freeze(
  Object.keys(lazilyLoadedIcons)
);
var isIconName = (value) => iconNames.includes(value);

// src/index.ts
var Icon2 = Icon;
Icon2.Stencil = IconStencil;
export {
  Icon2 as Icon,
  iconNames,
  iconSize,
  isIconName
};
